import { FunctionComponent, useRef, useState } from "react";
import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Input, Text, VStack } from "@chakra-ui/react";
import { useCandidateIds } from "../CandidatePage/CandidateIdsContext";
import { useRequestSave } from "../../api";
import { Request, RequestArgs, RequestArgsBody } from "@am-tax/wotc-common";
import { SubmitHandler, useForm } from "react-hook-form";
import { FileContainer, FileUploader, useToast } from "@am-tax/fe-core";
import { useCreateRequestUploadFile } from "../../api/hooks/useCreateRequestUploadFile";

type FormValues = Partial<Omit<RequestArgsBody, "years">> & { years?: string };

interface RequestFormProps {
    onClose: () => void;
}

export const CreateRequestForm: FunctionComponent<RequestFormProps> = ({ onClose }) => {
    const toast = useToast();
    const { cId, eId, candidateId } = useCandidateIds();
    const requestId = undefined;
    const request = {} as Request;

    // ---- Code to Load the Form ----
    const formValues: FormValues = {
        name: request?.name,
        description: request?.description,
        status: request?.status,
    };
    const formRef = useRef<HTMLFormElement>(null);
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        values: formValues,
    });

    // ---- Setup Submit Handlers for saving the Form ----
    const saveRequest = useRequestSave();
    const uploadFilesToRequest = useCreateRequestUploadFile();
    const [uploading, setUploading] = useState(false);
    const onSubmit: SubmitHandler<FormValues> = async data => {
        setUploading(true);
        const args: RequestArgs = {
            clientId: cId,
            entityId: eId,
            candidateId: candidateId!, // We can't get here without this
            old: undefined,
            new: {
                id: requestId,
                name: data.name!, // This is required by react-hook-form
                description: data.description,
                status: data.status!, // This is required by react-hook-form
            },
        };
        saveRequest.mutate(args, {
            onSuccess: data => {
                if (fileQueue.length > 0) {
                    for (const fileContainer of fileQueue) {
                        uploadFilesToRequest.mutate(
                            {
                                clientId: cId,
                                entityId: eId,
                                candidateId: candidateId!,
                                requestId: data.id,
                                fileContainer,
                            },
                            {
                                onError: () => {
                                    setUploading(false);
                                },
                            }
                        );
                    }
                } else {
                    toast({
                        title: "Created",
                        description: "Document request created successfully.",
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                    });
                    goBackToAllRequests();
                }
            },
        });
    };
    const formSubmit = handleSubmit(onSubmit);

    // ---- Setup File Upload mockApiResponses ----
    const [fileQueue, setFileQueue] = useState<FileContainer[]>([]);
    const fileQueueRef = useRef<FileContainer[]>(fileQueue);

    const onFileAdded = async (fileContainer: FileContainer) => {
        const queue = [...fileQueueRef.current];
        queue.push(fileContainer);
        setFileQueue(queue);
        fileQueueRef.current = queue;
    };
    const onFileRemoved = async (fileContainer: FileContainer) => {
        const updatedQueue = [];
        const queue = [...fileQueueRef.current];
        for (const file of queue) {
            if (file.index !== fileContainer.index) {
                updatedQueue.push(file);
            }
        }
        setFileQueue(updatedQueue);
        fileQueueRef.current = updatedQueue;
    };

    const onFileUploaded = async (fileContainer: FileContainer, uploadsRemaining: number) => {
        if (uploadsRemaining === 0) {
            setUploading(false);
            setFileQueue([]);
            toast({
                title: "Created",
                description: "Document request created and files uploaded successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            goBackToAllRequests();
        }
    };

    // ---- Navigation Helper ----
    const goBackToAllRequests = onClose;

    return (
        <>
            <form ref={formRef} onSubmit={formSubmit}>
                <fieldset disabled={saveRequest.isLoading || uploading}>
                    <VStack gap={"1rem"}>
                        <Flex alignItems={"stretch"} width={"full"}>
                            <VStack spacing="1rem" alignItems="normal" width={"full"}>
                                <FormControl isInvalid={!!errors?.name}>
                                    <FormLabel>Request Name:</FormLabel>
                                    <Input placeholder={"Name"} {...register("name", { required: "Request Name is required" })} />
                                    <FormErrorMessage>{errors?.name?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!errors?.description}>
                                    <FormLabel>Description:</FormLabel>
                                    <Input placeholder={"Description"} {...register("description")} />
                                </FormControl>
                            </VStack>
                        </Flex>
                        <Flex alignItems={"stretch"} width={"full"}>
                            <VStack spacing="1rem" alignItems="stretch">
                                <FormControl isInvalid={!!errors?.status}>
                                    <FormLabel>Documents:</FormLabel>
                                    <Text>{request?.documents ? "" : "No files uploaded"}</Text>
                                    <FormErrorMessage>{errors?.status?.message}</FormErrorMessage>
                                </FormControl>
                            </VStack>
                        </Flex>
                        <FileUploader onFileAdded={onFileAdded} onFileCancelled={onFileRemoved} onFileUploaded={onFileUploaded} />
                        <Flex justifyContent="flex-end" mt={"2rem"} width={"full"}>
                            <Button type="submit" variant={"primary"} mr={3} isLoading={uploading} isDisabled={uploading} loadingText={"Saving"}>
                                Create
                            </Button>
                            <Button variant="ghost" onClick={goBackToAllRequests}>
                                Cancel
                            </Button>
                        </Flex>
                    </VStack>
                </fieldset>
            </form>
        </>
    );
};
